import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import { WidgetView, WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useQueryGetComponentColumns } from 'api/queries/getComponentColumns.query';
import { useQueryGetTableColumns } from 'api/queries/getTableColumns.query';
import { useQueryGetWidgetColumns } from 'api/queries/getWidgetColumns.query';

import { ColumnCommonConfig } from 'components/common/Tables/columnsCommonConfig';

import { ConfirmWidgetPageWrapperUrlParams } from 'components/pages/ConfirmWidgetPage/ConfirmWidgetPageWrapper';

export type ISortBy = {
    id: ColumnCommonConfig;
    desc?: boolean;
};

export interface ITableColumnState {
    hiddenColumns?: ColumnCommonConfig[];
    sortBy?: ISortBy[];
    groupBy?: ColumnCommonConfig[];
    columnOrder?: ColumnCommonConfig[];
}

interface useTableCurrentStateProps {
    componentKey: string;
    widgetId?: string;
    initialState: ITableColumnState;
    widgetViewMode?: WidgetViewMode;
}

/**
 * Hook that initializes table column configuration from server data.
 * Determines the appropriate data source based on the current view context (Page, Pinboard, or Report - view/edit):
 */
export default function useTableCurrentState({
    componentKey,
    widgetId,
    initialState,
    widgetViewMode = WidgetView.page,
}: useTableCurrentStateProps): ITableColumnState | null {
    const [state, setState] = useState<ITableColumnState | null>(null);
    const { widgetId: widgetIdUrlParam } = useParams<ConfirmWidgetPageWrapperUrlParams>(); //get widgetId in case of editing widget in report

    // widgets view mode state
    const isWidgetInReportEditOrView =
        WidgetViewModeUtils.isReportView(widgetViewMode) || WidgetViewModeUtils.isReportEditWidget(widgetViewMode);
    const isWidgetInPage =
        !widgetId &&
        !WidgetViewModeUtils.isReportEditWidget(widgetViewMode) &&
        !WidgetViewModeUtils.isReportView(widgetViewMode);
    const isWidgetInPinboard = Boolean(widgetId) && !WidgetViewModeUtils.isReportView(widgetViewMode);

    // queries
    const { data: widgetInPageColumnsState } = useQueryGetTableColumns({ componentKey });
    const { data: widgetInPinboardColumnsState } = useQueryGetWidgetColumns({
        widgetId,
        enabled: isWidgetInPinboard,
    });
    const { data: widgetInReportColumnsState } = useQueryGetComponentColumns({
        componentId: widgetId ?? widgetIdUrlParam?.toString(),
        enabled: isWidgetInReportEditOrView,
    });

    const widgetInReportOrPinboardColumnsState = isWidgetInReportEditOrView
        ? widgetInReportColumnsState
        : widgetInPinboardColumnsState;

    useEffect(() => {
        // If is widget in pinboard or report edit/view
        if ((isWidgetInPinboard || isWidgetInReportEditOrView) && Boolean(widgetInReportOrPinboardColumnsState)) {
            setState({ ...initialState, ...widgetInReportOrPinboardColumnsState });
        }
        // If is widget in page
        if (isWidgetInPage && Boolean(widgetInPageColumnsState)) {
            setState({ ...initialState, ...widgetInPageColumnsState });
        }
        // if saved page state not exists AND not in report or pinboard <-OR-> saved state for pinboard or report and you are in pinboard or report
        if (
            (widgetInPageColumnsState === null &&
                !(WidgetViewModeUtils.isReportView(widgetViewMode) || isWidgetInPinboard)) ||
            (widgetInReportOrPinboardColumnsState === null &&
                (WidgetViewModeUtils.isReportView(widgetViewMode) || isWidgetInPinboard))
        ) {
            setState(initialState);
        }
    }, [
        componentKey,
        initialState,
        widgetInReportOrPinboardColumnsState,
        widgetInPageColumnsState,
        widgetId,
        widgetViewMode,
    ]);

    return state;
}
